import { useNavigate } from "@/router";
import type { Path } from "@/router";
import Avatar from "../Avatar";
import AlgoliaQuickSearch from "../AlgoliaSearch/AlgoliaQuickSearch";
import { useEffect, useState, useMemo } from "react";
import algoliasearch from "algoliasearch/lite";

interface JobHit {
  jobId: string;
  jobTitle: string;
  objectID: string;
  facilities?: Array<{
    facility_name?: string;
    city?: string;
    state?: string;
  }>;
  employer?: {
    name?: string;
  };
}

interface AlgoliaJobsSearchProps {
  onSelect?: (jobId: string, jobTitle: string, objectId?: string) => void;
  onClear?: () => void;
  className?: string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  defaultValue?: string;
  jobPostId?: string;
  supportingText?: string;
}

export default function AlgoliaJobsSearch({
  className,
  placeholder,
  onSelect,
  onClear,
  label,
  disabled,
  defaultValue,
  jobPostId,
  supportingText
}: AlgoliaJobsSearchProps) {
  const navigate = useNavigate();
  const indexName = import.meta.env.VITE_JOB_POST_SEARCH_INDEX_NAME;

  // each index we're pointing to will create it's own client.
  const [jobName, setJobName] = useState<string | undefined>(defaultValue);
  const appId = import.meta.env.VITE_ALGOLIA_APP_ID;
  const apiKey = import.meta.env.VITE_ALGOLIA_API_KEY;

  // Use useMemo instead of useRef for searchClient to ensure stability
  const searchClient = useMemo(() => {
    return algoliasearch(appId, apiKey);
  }, [appId, apiKey]);

  const initialUiState = useMemo(() => {
    if (jobPostId) {
      return {
        [indexName]: {
          disjunctiveFacetsRefinements: {
            jobId: [jobPostId]
          }
        }
      };
    }
    return undefined;
  }, [jobPostId, indexName]);

  // Fetch job details directly from Algolia if jobPostId is provided
  useEffect(() => {
    if (jobPostId) {
      // Only fetch if we don't already have a job name
      const shouldFetch = !jobName || jobName === "";

      if (shouldFetch) {
        const index = searchClient.initIndex(indexName);

        // Try searching with a broader filter to catch either jobId or objectID
        index
          .search("", {
            filters: `jobId:"${jobPostId}" OR objectID:"${jobPostId}"`,
            attributesToRetrieve: [
              "jobTitle",
              "jobId",
              "objectID",
              "facilities",
              "offices",
              "employer",
              "job_category",
              "employment_type",
              "pay_schedule",
              "base_compensation",
              "base_compensation_max",
              "case_status"
            ]
          })
          .then((result) => {
            if (result.hits && result.hits.length > 0) {
              const job = result.hits[0] as JobHit;
              if (job.jobTitle) {
                setJobName(job.jobTitle);

                // Trigger onSelect with the job information if available
                if (onSelect && job.jobId) {
                  onSelect(job.jobId, job.jobTitle, job.objectID);
                }
              }
            }
          })
          .catch((error: Error) => {
            console.error("Error fetching job details from Algolia:", error);
          });
      }
    }
  }, [jobPostId, indexName, onSelect, searchClient, jobName]);

  const handleClear = () => {
    setJobName(undefined);

    if (onClear) {
      onClear();
    }
  };

  const renderJobHit = (hit: JobHit, onSelect: (hit: JobHit) => void) => {
    const facilityName = hit.facilities?.[0]?.facility_name || "Unknown Facility";
    const facilityLocation =
      hit.facilities?.[0]?.city && hit.facilities?.[0]?.state
        ? `${hit.facilities[0].city}, ${hit.facilities[0].state}`
        : "";
    const employerName = hit.employer?.name || "";

    return (
      <div key={hit.objectID} className="mb-2 cursor-pointer" onClick={() => onSelect(hit)}>
        <div className="flex items-center gap-4 p-2 hover:bg-sys-brand-surface-container hover:shadow-sm rounded-md transition-all duration-200 ease-in-out">
          <Avatar size="medium" iconName="business_center" />
          <div className="flex flex-col">
            <div className="text-label-medium text-sys-brand-on-surface-variant">{employerName}</div>
            <div className="text-body-large text-sys-brand-on-surface">{hit.jobTitle || "Job Position"}</div>
            <div className="text-body-medium text-sys-brand-on-surface-variant">{facilityName}</div>
            <div className="text-body-small text-sys-brand-on-surface-variant">{facilityLocation}</div>
          </div>
        </div>
      </div>
    );
  };

  const handleJobSelect = (hit: JobHit) => {
    if (onSelect) {
      onSelect(hit.jobId, hit.jobTitle, hit.objectID);
    } else {
      // Default navigation behavior if no onSelect provided
      const currentPath = location.pathname;
      const basePath = currentPath.split("/").slice(0, -1).join("/");
      const path = `${basePath}/job-post/${hit.jobId}/details` as Path;
      navigate(path);
    }
  };

  return (
    <AlgoliaQuickSearch<JobHit>
      indexName={indexName}
      className={className}
      placeholder={placeholder || "Search for jobs"}
      label={label}
      disabled={disabled}
      defaultValue={jobName}
      onSelect={handleJobSelect}
      renderHit={renderJobHit}
      noResultsMessage="No job posts found"
      displayKey="jobTitle"
      initialUiState={initialUiState}
      onClear={handleClear}
      supportingText={supportingText}
    />
  );
}
